/**
 * Anzeige-Helfer des Posteingangs — Namen, Initialen, Farben, Datumsformate.
 *
 * Aus pages/EmailsList.tsx herausgeloest (Schritt G). Reine Funktionen ohne
 * React und ohne Zustand: der einfachste Teil der Datei, und der, den auch
 * andere Ansichten brauchen.
 */

export function senderInitials(s: string): string {
  const name = s.replace(/<[^>]*>/g, "").trim()
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return "?"
}
export function prettySender(sender?: string, email?: string): string {
  const name = (sender || "").replace(/<[^>]*>/g, "").trim()
  return name || email || "—"
}
export const AVATAR_PALETTE = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#8b5cf6", "#ec4899", "#84cc16", "#f97316", "#3b82f6"]
export function avatarColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = ((h << 5) - h + seed.charCodeAt(i)) | 0
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length]
}
/** Listen-Datum: heute nur Uhrzeit, gestern "gestern 21:43", diese Woche
 *  Wochentag, sonst Datum. Nach Kalendertag, nicht 24-h-Fenster — sonst stand
 *  bei einer Mail von gestern Abend nur die Uhrzeit (Paul 29.10.2026). */
export function fmtDate(iso?: string | null, now: Date = new Date()): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const abstand = Math.round((tag(now) - tag(d)) / 86400000)
  const uhr = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
  if (abstand <= 0) return uhr
  if (abstand === 1) return `gestern ${uhr}`
  if (abstand < 7) return d.toLocaleDateString("de-DE", { weekday: "short" })
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })
}
/** Kurz mit Uhrzeit für Listen: "heute 17:11" / "gestern 09:05" / "26.10. 14:20"
 *  (Paul 28.10.2026: „Zur Kenntnis“ bitte mit Uhrzeit/Datum). */
export function fmtZeitpunkt(iso?: string | null, jetzt: Date = new Date()): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  const uhr = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
  const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const abstand = Math.round((tag(jetzt) - tag(d)) / 86400000)
  if (abstand === 0) return `heute ${uhr}`
  if (abstand === 1) return `gestern ${uhr}`
  const datum = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit",
    ...(d.getFullYear() !== jetzt.getFullYear() ? { year: "2-digit" } : {}) })
  return `${datum} ${uhr}`
}
/** Langformat für Detail-Header: "Heute, 21:43" / "Gestern, 21:43" / "Mo, 21.04.2026, 21:43" */
export function fmtDateLong(iso?: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  const now = new Date()
  const time = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
  const sameDay = d.toDateString() === now.toDateString()
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1)
  if (sameDay) return `Heute, ${time}`
  if (d.toDateString() === yesterday.toDateString()) return `Gestern, ${time}`
  const diffDays = (now.getTime() - d.getTime()) / 86400000
  if (diffDays < 7) return `${d.toLocaleDateString("de-DE", { weekday: "short" })}, ${time}`
  return `${d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}, ${time}`
}
/** Nimmt "Paul Lux <p@x.de>" → "Paul Lux", Fallback: der Mail-Teil */
export function cleanSender(s?: string | null): string {
  if (!s) return ""
  const m = s.match(/^(.*?)\s*<[^>]+>$/)
  if (m && m[1].trim()) return m[1].trim().replace(/^"|"$/g, "")
  return s
}
/** Nimmt "Paul Lux <p@x.de>" → "p@x.de"; reine Mail wird unveraendert
 *  zurueckgegeben; ohne Mail-Teil leer. */
export function extractEmail(s?: string | null): string {
  if (!s) return ""
  const m = s.match(/<([^>]+@[^>]+)>/)
  if (m) return m[1].trim()
  // Fallback: bare email "foo@bar.de"
  const bare = s.trim().match(/^[^\s<>]+@[^\s<>]+$/)
  return bare ? bare[0] : ""
}
export function avatarInitials(s?: string | null): string {
  const name = cleanSender(s) || s || "?"
  const parts = name.replace(/[<>"]/g, "").trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return (parts[0] || "?").slice(0, 2).toUpperCase()
}
